import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../store';
import { resetInterviewData } from '../store/interviewSlice';
import { fetchInterviewDetail } from '../services/aiService';
import { trackViewReport } from '../services/behaviorTracker';
import InterviewReport from '../components/InterviewReport';
import type {
  Message,
  InterviewReport as InterviewReportType,
  InterviewConfig,
  InterviewerConfig,
} from '../types';

export default function ReportPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  // 判断来源：从生成页进入时不显示返回按钮
  const fromGenerating = searchParams.get('from') === 'generating';

  // 从 Redux 获取 config 数据和加载状态
  const configData = useSelector((state: RootState) => state.config.data);
  const configLoading = useSelector(
    (state: RootState) => state.config.isLoading,
  );

  // 加载状态和详情数据
  const [loading, setLoading] = useState(false);
  const [detailData, setDetailData] = useState<{
    messages?: Message[];
    report?: InterviewReportType;
    interviewerId?: string;
    jobType?: string;
    jobLevel?: string;
    interviewType?: string;
  } | null>(null);
  const [error, setError] = useState('');

  // 判断是否有 URL 参数 id
  const hasId = !!id;
  const interviewId = id ? parseInt(id, 10) : null;

  // 没有 ID 时直接跳转到首页
  useEffect(() => {
    if (!hasId) {
      navigate('/', { replace: true });
    }
  }, [hasId, navigate]);

  // 进入页面时滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 等待 config 加载完成后，再请求报告详情
  useEffect(() => {
    // 没有 ID 不请求
    if (!hasId || !interviewId) {
      return;
    }

    // config 还在加载中，等待
    if (configLoading) {
      return;
    }

    const loadDetail = async () => {
      setLoading(true);
      setError('');
      try {
        const detail = await fetchInterviewDetail(interviewId);
        if (detail) {
          // 记录查看报告行为
          trackViewReport(interviewId).catch(() => {
            // 记录失败不影响主流程
          });

          setDetailData({
            messages: detail.messages || [],
            report: detail.report || {
              totalScore: detail.score,
              dimensions: {
                expression: Math.round(detail.score * 0.95),
                technical: Math.round(detail.score * 0.9),
                logic: Math.round(detail.score * 0.92),
                communication: Math.round(detail.score * 0.94),
                professionalism: Math.round(detail.score * 0.93),
                learning: Math.round(detail.score * 0.91),
                overall: Math.round(detail.score * 0.93),
              },
              strengths: [],
              suggestions: [],
              detailedAnalysis: {
                expression: '',
                technical: '',
                logic: '',
                communication: '',
                professionalism: '',
                learning: '',
              },
              nextSteps: [],
            },
            interviewerId: detail.interviewer_id,
            jobType: detail.job_type,
            jobLevel: detail.job_level,
            interviewType: detail.interview_type,
          });
        } else {
          setError('面试记录不存在');
        }
      } catch (err) {
        setError('获取面试详情失败');
      } finally {
        setLoading(false);
      }
    };

    setTimeout(() => {
      loadDetail();
    }, 1000);
  }, [hasId, interviewId, configLoading]);

  // 通过 interviewerId 从 Redux config 中查找对应的面试官信息
  const interviewer = useMemo<InterviewerConfig | undefined>(() => {
    if (!detailData?.interviewerId || !configData?.interviewers) {
      return undefined;
    }
    return configData.interviewers.find(
      (i) => i.id === detailData.interviewerId,
    );
  }, [detailData?.interviewerId, configData?.interviewers]);

  // 构建完整的 InterviewConfig
  const interviewConfig = useMemo<InterviewConfig | undefined>(() => {
    if (!detailData) {
      return undefined;
    }
    return {
      jobType: detailData.jobType || '',
      jobCategory: detailData.jobType || '',
      level: (detailData.jobLevel as InterviewConfig['level']) || '中级',
      type: (detailData.interviewType as InterviewConfig['type']) || '综合面试',
      interviewer: interviewer || {
        id: detailData.interviewerId || '',
        name: '面试官',
        avatar: '',
        gender: 1,
        personality: null,
        voice: '',
        scenario: null,
        status: 1,
      },
    };
  }, [detailData, interviewer]);

  const handleRestart = async () => {
    // 使用 Redux 重置面试数据（保留配置）
    await dispatch(resetInterviewData());
    // 返回到面试准备页（loading 页面会生成新的开场白）
    navigate('/loading');
  };

  const handleBack = () => {
    // 返回面试历史页面
    navigate('/history');
  };

  // 没有 ID 时返回 null（会被 useEffect 重定向到首页）
  if (!hasId) {
    return null;
  }

  // 加载中显示（包括 config 加载和报告详情加载）
  if (configLoading || loading || !detailData?.report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400">加载中...</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => navigate('/history')}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-400 transition-colors"
          >
            返回历史记录
          </button>
        </div>
      </div>
    );
  }

  // 没有数据时返回 null
  // if (!detailData?.report) {
  //   return null;
  // }

  return (
    <InterviewReport
      messages={detailData.messages || []}
      report={detailData.report}
      onRestart={handleRestart}
      config={interviewConfig}
      showBackButton={!fromGenerating}
      onBack={handleBack}
    />
  );
}
